import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { useDispatch } from "react-redux";
import CollapseHeader from "../../../core/common/collapse-header";
import { all_routes } from "../../router/all_routes";
import { useFunnels } from "../../../hooks/useFunnels";
import { apiService } from "../../../services/api";
import { Stage, EditableFunnel } from '../../../types/funnels';
import { Offcanvas, Modal } from 'bootstrap';
import dragula, { Drake } from 'dragula';
import 'bootstrap/dist/css/bootstrap.min.css';
import { ProtectedComponent } from "../../../components/ProtectedComponent";
import { usePermissions } from '../../../hooks/usePermissions';

const route = all_routes;

interface TableColumn {
  title: string;
  dataIndex?: string;
  render?: (text: any, record: EditableFunnel) => React.ReactNode;
  sorter?: (a: EditableFunnel, b: EditableFunnel) => number;
}

interface NewStage {
  nome: string;
  descricao?: string;
  gera_contrato?: boolean; // NOVO CAMPO
}

interface FunnelFormData {
  nome: string;
  descricao?: string;
  ativo: boolean;
  etapas: NewStage[];
}

interface EditingStage {
  id?: number;
  nome: string;
  descricao?: string;
  ordem?: number;
  gera_contrato?: boolean; // NOVO CAMPO
}

export const Funil = () => {
  const dispatch = useDispatch();
  const { funnels, loading, error, refetchFunnels } = useFunnels();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingFunnelId, setDeletingFunnelId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredFunnels, setFilteredFunnels] = useState<typeof funnels>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Refs para os containers do Dragula
  const newStagesContainerRef = useRef<HTMLDivElement>(null);
  const editStagesContainerRef = useRef<HTMLDivElement>(null);
  const dragulaNewRef = useRef<Drake | null>(null);
  const dragulaEditRef = useRef<Drake | null>(null);

  // Estado para controlar o funil em edição
  const [editingFunnel, setEditingFunnel] = useState<EditableFunnel | null>(null);
  const [newFunnel, setNewFunnel] = useState<FunnelFormData>({
    nome: '',
    descricao: '',
    ativo: true,
    etapas: []
  });

  const [currentStage, setCurrentStage] = useState<NewStage>({
    nome: '',
    descricao: '',
    gera_contrato: false // NOVO CAMPO
  });
  const [editingStage, setEditingStage] = useState<EditingStage>({
    nome: '',
    descricao: '',
    gera_contrato: false // NOVO CAMPO
  });
  const [editingFunnelStages, setEditingFunnelStages] = useState<EditingStage[]>([]);

  // NOVA FUNÇÃO: Renderizar ícone de contrato
  const renderContratoIcon = (geraContrato: boolean) => {
    if (geraContrato) {
      return (
        <span
          className="badge bg-success-light text-success ms-2"
          title="Esta etapa gera contratos automaticamente"
          style={{ fontSize: '10px', padding: '2px 6px' }}
        >
          <i className="ti ti-file-invoice me-1"></i>
          Gera Contrato
        </span>
      );
    }
    return null;
  };

  // Inicializar Dragula para novo funil
  useEffect(() => {
    if (newStagesContainerRef.current) {
      // Destroy existing instance
      if (dragulaNewRef.current) {
        dragulaNewRef.current.destroy();
      }

      dragulaNewRef.current = dragula([newStagesContainerRef.current], {
        moves: (el?: Element, container?: Element, handle?: Element): boolean => {
          if (!handle) return true;
          return handle.classList.contains('drag-handle') ||
            handle.closest('.drag-handle') !== null ||
            handle.closest('.pipeline-item') !== null;
        },
        direction: 'vertical',
        revertOnSpill: true,
        removeOnSpill: false
      });

      dragulaNewRef.current.on('drop', (el: Element, target: Element, source: Element, sibling: Element | null) => {
        // Obter o índice original do elemento arrastado
        const originalIndex = parseInt(el.getAttribute('data-index') || '0');

        // Calcular o novo índice baseado na posição do sibling
        let newIndex = 0;
        if (sibling) {
          newIndex = parseInt(sibling.getAttribute('data-index') || '0');
        } else {
          // Se não há sibling, está sendo colocado no final
          newIndex = target.children.length - 1;
        }

        console.log('Dragula Drop:', { originalIndex, newIndex });

        // Atualizar o array de etapas
        setNewFunnel(prev => {
          const newStages = [...prev.etapas];

          // Remover o item da posição original
          const [movedItem] = newStages.splice(originalIndex, 1);

          // Inserir na nova posição
          newStages.splice(newIndex, 0, movedItem);

          console.log('Novo array de etapas:', newStages.map((stage, idx) => ({
            nome: stage.nome,
            novaOrdem: idx + 1
          })));

          return {
            ...prev,
            etapas: newStages
          };
        });
      });
    }

    return () => {
      if (dragulaNewRef.current) {
        dragulaNewRef.current.destroy();
        dragulaNewRef.current = null;
      }
    };
  }, [newFunnel.etapas.length]);

  // Inicializar Dragula para edição de funil
  useEffect(() => {
    if (editStagesContainerRef.current) {
      // Destroy existing instance
      if (dragulaEditRef.current) {
        dragulaEditRef.current.destroy();
      }

      dragulaEditRef.current = dragula([editStagesContainerRef.current], {
        moves: (el?: Element, container?: Element, handle?: Element): boolean => {
          if (!handle) return true;
          return handle.classList.contains('drag-handle') ||
            handle.closest('.drag-handle') !== null ||
            handle.closest('.pipeline-item') !== null;
        },
        direction: 'vertical',
        revertOnSpill: true,
        removeOnSpill: false
      });

      dragulaEditRef.current.on('drop', (el: Element, target: Element, source: Element, sibling: Element | null) => {
        // Obter o índice original do elemento arrastado
        const originalIndex = parseInt(el.getAttribute('data-index') || '0');

        // Calcular o novo índice baseado na posição do sibling
        let newIndex = 0;
        if (sibling) {
          newIndex = parseInt(sibling.getAttribute('data-index') || '0');
        } else {
          // Se não há sibling, está sendo colocado no final
          newIndex = target.children.length - 1;
        }

        console.log('Dragula Edit Drop:', { originalIndex, newIndex });

        // Atualizar o array de etapas com nova ordem
        setEditingFunnelStages(prev => {
          const newStages = [...prev];

          // Remover o item da posição original
          const [movedItem] = newStages.splice(originalIndex, 1);

          // Inserir na nova posição
          newStages.splice(newIndex, 0, movedItem);

          // Atualizar as ordens baseado na nova posição
          const updatedStages = newStages.map((stage, index) => ({
            ...stage,
            ordem: index + 1
          }));

          console.log('Etapas reordenadas:', updatedStages.map(stage => ({
            id: stage.id,
            nome: stage.nome,
            novaOrdem: stage.ordem
          })));

          return updatedStages;
        });
      });
    }

    return () => {
      if (dragulaEditRef.current) {
        dragulaEditRef.current.destroy();
        dragulaEditRef.current = null;
      }
    };
  }, [editingFunnelStages.length]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (dragulaNewRef.current) {
        dragulaNewRef.current.destroy();
      }
      if (dragulaEditRef.current) {
        dragulaEditRef.current.destroy();
      }
    };
  }, []);

  // Efeito para inicializar a lista filtrada
  useEffect(() => {
    setFilteredFunnels(funnels as any);
  }, [funnels]);

  // E na função de filtragem:
  useEffect(() => {
    setIsSearching(true);
    const debounceTimeout = setTimeout(() => {
      if (!searchText.trim()) {
        setFilteredFunnels(funnels as any);
        setIsSearching(false);
        return;
      }

      const searchTerms = searchText.toLowerCase().trim().split(' ');

      const filtered = funnels.filter(funnel => {
        // Campos a serem pesquisados
        const searchableFields = [
          funnel.nome,
          funnel.descricao,
          funnel.ativo === 1 ? 'ativo' : 'inativo',
          funnel.data_criacao,
          funnel.total_etapas.toString()
        ].filter(Boolean).map(field => field?.toLowerCase() || '');

        // Verificar se todos os termos de pesquisa estão presentes em pelo menos um dos campos
        return searchTerms.every(term =>
          searchableFields.some(field => field.includes(term))
        );
      });

      setFilteredFunnels(filtered as any);
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [searchText, funnels]);

  // Função para limpar texto de pesquisa
  const handleClearSearch = () => {
    setSearchText('');
  };

  // Função para formatar a data
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  // Função para preparar a edição
  const handleEdit = (funnel: EditableFunnel) => {
    setEditingFunnel(funnel);
  };

  // Função auxiliar para converter EditingStage para Stage
  const convertToStage = (editingStage: EditingStage, index: number): Stage => {
    return {
      nome: editingStage.nome,
      descricao: editingStage.descricao,
      ordem: editingStage.ordem || index + 1,
      gera_contrato: editingStage.gera_contrato || false // NOVO CAMPO
    };
  };

  // MODIFICAÇÃO: Adicione estas funções para gerenciar a edição do funil
  const handleInitEdit = (funnel: EditableFunnel) => {
    setEditingFunnel(funnel);
    setEditingFunnelStages(funnel.etapas.map(etapa => ({
      id: etapa.id,
      nome: etapa.nome,
      descricao: etapa.descricao,
      ordem: etapa.ordem,
      gera_contrato: etapa.gera_contrato || false // NOVO CAMPO
    })));
  };

  const handleEditStage = (stage: EditingStage) => {
    setEditingStage({
      id: stage.id,
      nome: stage.nome,
      descricao: stage.descricao,
      ordem: stage.ordem,
      gera_contrato: stage.gera_contrato || false // NOVO CAMPO
    });
  };

  const handleSaveStageEdit = async () => {
    if (!editingFunnel || !editingStage.id) return;

    setEditingFunnelStages(prevStages =>
      prevStages.map(stage =>
        stage.id === editingStage.id
          ? { ...stage, ...editingStage }
          : stage
      )
    );

    // Limpa o estado de edição da etapa
    setEditingStage({ nome: '', descricao: '', gera_contrato: false }); // MODIFICADO

    // Fecha o modal de edição
    const modalElement = document.getElementById('edit_stage');
    if (modalElement) {
      const modal = Modal.getInstance(modalElement);
      modal?.hide();
    }
  };

  const handleAddStageToFunnel = () => {
    if (!editingFunnel) return;

    if (!editingStage.nome.trim()) {
      alert('O nome da etapa é obrigatório');
      return;
    }

    const newEditingStage: EditingStage = {
      id: 0,
      nome: editingStage.nome,
      descricao: editingStage.descricao,
      ordem: editingFunnelStages.length + 1,
      gera_contrato: editingStage.gera_contrato || false // NOVO CAMPO
    };

    setEditingFunnelStages(prevStages => [...prevStages, newEditingStage]);

    setEditingStage({
      nome: '',
      descricao: '',
      gera_contrato: false // NOVO CAMPO
    });

    const modalElement = document.getElementById('add_edit_funnel_stage');
    if (modalElement) {
      const modal = Modal.getInstance(modalElement);
      modal?.hide();
    }
  };

  const handleRemoveStageFromFunnel = (stageId: number) => {
    setEditingFunnelStages(prevStages =>
      prevStages.filter(stage => stage.id !== stageId)
    );
  };

  const handleSaveEdit = async () => {
    if (!editingFunnel) return;

    try {
      setIsSubmitting(true);

      // Preparar as etapas garantindo ordem sequencial correta
      const convertedStages = editingFunnelStages.map((stage, index) => {
        const baseStage = {
          nome: stage.nome,
          descricao: stage.descricao || '',
          ordem: index + 1, // Ordem baseada na posição atual no array
          gera_contrato: stage.gera_contrato || false
        };

        // Adiciona o ID apenas se a etapa já existir
        if (stage.id && stage.id > 0) {
          return { ...baseStage, id: stage.id };
        }

        // Para novas etapas, retorna sem o campo ID
        return baseStage;
      });

      console.log('Etapas a serem enviadas para API:', convertedStages);

      // Prepara os dados para envio
      const updatedFunnel = {
        nome: editingFunnel.nome,
        descricao: editingFunnel.descricao,
        ativo: editingFunnel.ativo,
        etapas: convertedStages
      };

      // Log do payload final para verificação
      console.log('Payload final para API:', {
        id: editingFunnel.id,
        ...updatedFunnel
      });

      // Envia para a API
      await apiService.updateFunil(editingFunnel.id, updatedFunnel);
      await refetchFunnels();

      // Fecha o offcanvas
      const offcanvasElement = document.getElementById('offcanvas_edit');
      if (offcanvasElement) {
        const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      // Mostra o modal de sucesso
      setTimeout(() => {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-open', 'offcanvas-open');

        const backdrops = document.querySelectorAll('.modal-backdrop, .offcanvas-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());

        const successModal = document.getElementById('success_edit_modal');
        if (successModal) {
          const modalInstance = new Modal(successModal);
          modalInstance.show();
        }
      }, 300);

      // Limpa os estados
      setEditingFunnel(null);
      setEditingFunnelStages([]);
      setEditingStage({ nome: '', descricao: '', gera_contrato: false });
    } catch (error) {
      console.error('Erro ao atualizar funil:', error);
      alert('Erro ao atualizar o funil. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para atualizar campos do funil
  const handleFunnelChange = <K extends keyof EditableFunnel>(
    field: K,
    value: EditableFunnel[K]
  ) => {
    setEditingFunnel((prev: EditableFunnel | null) =>
      prev ? { ...prev, [field]: value } : null
    );
  };

  // Função para limpar o estado de edição
  const handleCancelEdit = () => {
    setEditingFunnel(null);
  };

  // Função para preparar a exclusão
  const handlePrepareDelete = (funnel: EditableFunnel) => {
    setDeletingFunnelId(funnel.id);
  };

  // Função para lidar com mudanças nos campos do novo funil
  const handleNewFunnelChange = (
    field: keyof FunnelFormData,
    value: string | boolean | NewStage[]
  ) => {
    setNewFunnel(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const limparModal = (idModal: string) => {
    // Obtém a instância do modal
    const elementoModal = document.getElementById(idModal);
    const instanciaModal = elementoModal ? Modal.getInstance(elementoModal) : null;

    // Fecha o modal se ele existir
    if (instanciaModal) {
      instanciaModal.hide();
    }

    // Função de limpeza
    const executarLimpeza = () => {
      // Restaura os estilos do body
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');

      // Remove as classes específicas do modal
      document.body.classList.remove('modal-open', 'offcanvas-open');

      // Remove todos os backdrops de modal
      const backdrops = document.querySelectorAll('.modal-backdrop, .offcanvas-backdrop');
      backdrops.forEach(backdrop => backdrop.remove());
    };

    // Se o modal ainda estiver em transição, aguarda finalizar
    if (elementoModal?.classList.contains('show')) {
      elementoModal.addEventListener('hidden.bs.modal', () => {
        executarLimpeza();
      }, { once: true });
    } else {
      executarLimpeza();
    }
  };

  // MODIFICAÇÃO: Função para adicionar uma nova etapa
  const handleAddStage = () => {
    if (!currentStage.nome.trim()) {
      alert('O nome da etapa é obrigatório');
      return;
    }

    setNewFunnel(prev => ({
      ...prev,
      etapas: [...prev.etapas, {
        nome: currentStage.nome,
        descricao: currentStage.descricao,
        gera_contrato: currentStage.gera_contrato || false // NOVO CAMPO
      }]
    }));

    // Limpa o formulário da etapa
    setCurrentStage({
      nome: '',
      descricao: '',
      gera_contrato: false // NOVO CAMPO
    });

    // Fecha o modal de adicionar etapa
    const modalElement = document.getElementById('add_stage');
    if (modalElement) {
      const modal = Modal.getInstance(modalElement);
      modal?.hide();
    }
  };

  // Função para remover uma etapa
  const handleRemoveStage = (index: number) => {
    setNewFunnel(prev => ({
      ...prev,
      etapas: prev.etapas.filter((_, i) => i !== index)
    }));
  };

  const handleCreateFunnel = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      if (!newFunnel.nome.trim()) {
        alert('O nome do funil é obrigatório');
        return;
      }

      if (newFunnel.etapas.length === 0) {
        alert('Adicione pelo menos uma etapa ao funil');
        return;
      }

      // Preparar etapas com ordem sequencial
      const etapasComOrdem = newFunnel.etapas.map((etapa, index) => ({
        ...etapa,
        ordem: index + 1 // Garantir ordem sequencial baseada na posição
      }));

      const funnelData = {
        nome: newFunnel.nome,
        descricao: newFunnel.descricao,
        ativo: newFunnel.ativo,
        etapas: etapasComOrdem
      };

      console.log('Criando funil com etapas ordenadas:', etapasComOrdem);

      const response = await apiService.createFunil(funnelData);
      await refetchFunnels();

      setNewFunnel({
        nome: '',
        descricao: '',
        ativo: true,
        etapas: []
      });

      // Resto da função permanece igual...
      const offcanvasElement = document.getElementById('offcanvas_pipeline');
      if (offcanvasElement) {
        const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      setTimeout(() => {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-open', 'offcanvas-open');
        const backdrops = document.querySelectorAll('.modal-backdrop, .offcanvas-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());

        const successModal = document.getElementById('success_modal');
        if (successModal) {
          const modalInstance = new Modal(successModal, {
            backdrop: 'static',
            keyboard: false
          });
          modalInstance.show();
        }
      }, 300);

    } catch (error) {
      console.error('Erro ao criar funil:', error);
      alert('Erro ao criar o funil. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Atualize a função handleDeleteFunnel para usar o novo utilitário de limpeza
  const handleDeleteFunnel = async () => {
    if (!deletingFunnelId) return;

    try {
      setIsDeleting(true);
      await apiService.deleteFunil(deletingFunnelId);
      await refetchFunnels();

      // Usa a função limparModal existente para limpar o modal de confirmação
      limparModal('delete_pipeline');
      setDeletingFunnelId(null);

      // Aguarda a limpeza completa antes de mostrar o modal de sucesso
      setTimeout(() => {
        const successModal = document.getElementById('success_delete_modal');
        if (successModal) {
          const modalInstance = new Modal(successModal);
          modalInstance.show();
        }
      }, 300);

    } catch (error) {
      console.error('Erro ao excluir funil:', error);
      alert('Erro ao excluir o funil. Por favor, tente novamente.');
    } finally {
      setIsDeleting(false);
    }
  };

  const limparModalESucesso = (idModal: string) => {
    // Obtém a instância do modal
    const elementoModal = document.getElementById(idModal);
    const instanciaModal = elementoModal ? Modal.getInstance(elementoModal) : null;

    // Fecha o modal se ele existir
    if (instanciaModal) {
      instanciaModal.hide();
    }

    // Função de limpeza completa
    const executarLimpeza = () => {
      // Restaura os estilos do body
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');

      // Remove as classes específicas do modal
      document.body.classList.remove('modal-open', 'offcanvas-open');

      // Remove todos os backdrops de modal
      const backdrops = document.querySelectorAll('.modal-backdrop, .offcanvas-backdrop');
      backdrops.forEach(backdrop => backdrop.remove());

      // Limpa o estado de exclusão se estiver presente
      setDeletingFunnelId(null);
    };

    // Se o modal ainda estiver em transição, aguarda finalizar
    if (elementoModal?.classList.contains('show')) {
      elementoModal.addEventListener('hidden.bs.modal', () => {
        executarLimpeza();
      }, { once: true });
    } else {
      executarLimpeza();
    }
  };

  useEffect(() => {
    return () => {
      // Cleanup ao desmontar o componente
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.classList.remove('modal-open', 'offcanvas-open');
      const backdrops = document.querySelectorAll('.modal-backdrop, .offcanvas-backdrop');
      backdrops.forEach(backdrop => backdrop.remove());
    };
  }, []);

  const columns: TableColumn[] = [
    {
      title: "Nome do Funil",
      dataIndex: "nome",
      sorter: (a, b) => a.nome.localeCompare(b.nome),
    },
    {
      title: "Descrição",
      dataIndex: "descricao",
      sorter: (a, b) => a.descricao.localeCompare(b.descricao),
    },
    {
      title: "Etapas",
      render: (_, record) => (
        <div className="pipeline-progress d-flex align-items-center">
          <div className="progress">
            <div
              className="progress-bar progress-bar-violet"
              role="progressbar"
              style={{ width: `${(record.total_etapas / 10) * 100}%` }}
            ></div>
          </div>
          <span>{record.total_etapas} etapas</span>
        </div>
      ),
      sorter: (a, b) => a.total_etapas - b.total_etapas,
    },
    {
      title: "Data de Criação",
      dataIndex: "data_criacao",
      render: (text) => formatDate(text),
      sorter: (a, b) => new Date(a.data_criacao).getTime() - new Date(b.data_criacao).getTime(),
    },
    {
      title: "Status",
      dataIndex: "ativo",
      render: (ativo) => (
        <div>
          {ativo === 1 ? (
            <span className="badge badge-pill badge-status bg-success">Ativo</span>
          ) : (
            <span className="badge badge-pill badge-status bg-danger">Inativo</span>
          )}
        </div>
      ),
      sorter: (a, b) => a.ativo - b.ativo,
    },
    {
      title: "Ações",
      render: (_, record) => (
        <div>
          <ProtectedComponent module="CONFIG_FUNIS" action="EDITAR">
            <Link
              to="#"
              className="dropdown-item"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvas_edit"
              onClick={() => handleInitEdit(record)}
            >
              <i className="ti ti-edit text-blue"></i> Editar
            </Link>
          </ProtectedComponent>
          <ProtectedComponent module="CONFIG_FUNIS" action="EXCLUIR">
            <Link
              to="#"
              className="dropdown-item"
              data-bs-toggle="modal"
              data-bs-target="#delete_pipeline"
              onClick={() => handlePrepareDelete(record)}
            >
              <i className="ti ti-trash text-danger"></i> Excluir
            </Link>
          </ProtectedComponent>
        </div>
      ),
    }
  ];

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Cabeçalho da Página */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">
                      Funis<span className="count-title">{filteredFunnels.length} funis</span>
                    </h4>
                  </div>
                  <div className="col-8 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="search-input position-relative">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            {isSearching ? (
                              <i className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <i className="ti ti-search" />
                            )}
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Pesquisar Funil"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                          {searchText && (
                            <button
                              className="btn btn-sm position-absolute"
                              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                              onClick={handleClearSearch}
                              type="button"
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Filtros e Ações */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      {/* Botão Ordenar */}
                      <div className="dropdown me-2">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-sort-ascending-2 me-2" />
                          Ordenar
                        </Link>
                        <div className="dropdown-menu">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Crescente
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Decrescente
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Visualizados Recentemente
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Adicionados Recentemente
                          </Link>
                        </div>
                      </div>

                      {/* Botão Exportar */}
                      <div className="dropdown">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-package-export me-2" />
                          Exportar
                        </Link>
                        <div className="dropdown-menu dropdown-menu-end">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-pdf text-danger me-1" />
                            Exportar como PDF
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-xls text-green me-1" />
                            Exportar como Excel
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Botões de Ação */}
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <ProtectedComponent module="CONFIG_FUNIS" action="CRIAR">
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_pipeline"
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Adicionar Funil
                        </Link>
                      </ProtectedComponent>
                    </div>
                  </div>

                  {/* Tabela de Funis */}
                  <div className="table-responsive custom-table">
                    {!isSearching && searchText && filteredFunnels.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="mb-3">
                          <i className="ti ti-search-off fs-3 text-muted"></i>
                        </div>
                        <h5 className="text-muted">Nenhum funil encontrado</h5>
                        <p className="text-muted">Sua pesquisa por "{searchText}" não retornou resultados.</p>
                        <button
                          className="btn btn-outline-primary mt-2"
                          onClick={handleClearSearch}
                        >
                          <i className="ti ti-x me-1"></i>
                          Limpar pesquisa
                        </button>
                      </div>
                    ) : (
                      <Table
                        dataSource={filteredFunnels.map(funnel => ({
                          ...funnel,
                          key: funnel.id.toString()
                        }))}
                        columns={columns}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Adicionar Novo Funil */}
      <div className="offcanvas offcanvas-end offcanvas-large" tabIndex={-1} id="offcanvas_pipeline">
        <div className="offcanvas-header border-bottom">
          <h4>Adicionar Novo Funil</h4>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas">
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={handleCreateFunnel}>
            <div className="mb-3">
              <label className="col-form-label">
                Nome do Funil <span className="text-danger">*</span>
              </label>
              <input
                className="form-control"
                type="text"
                value={newFunnel.nome}
                onChange={(e) => handleNewFunnelChange('nome', e.target.value)}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">
                Descrição
              </label>
              <textarea
                className="form-control"
                value={newFunnel.descricao}
                onChange={(e) => handleNewFunnelChange('descricao', e.target.value)}
              />
            </div>

            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="funnel-status"
                  checked={newFunnel.ativo}
                  onChange={(e) => handleNewFunnelChange('ativo', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="funnel-status">
                  Ativo
                </label>
              </div>
            </div>

            <div className="mb-3">
              <div className="pipe-title d-flex align-items-center justify-content-between">
                <h5 className="form-title">Etapas do Funil</h5>
                <Link
                  to="#"
                  className="add-stage"
                  data-bs-toggle="modal"
                  data-bs-target="#add_stage"
                >
                  <i className="ti ti-square-rounded-plus" />
                  Adicionar Nova
                </Link>
              </div>

              {/* Lista de etapas com Dragula */}
              <div className="pipeline-listing">
                {newFunnel.etapas.length > 0 && (
                  <div className="mb-3">
                    <div className="alert alert-info d-flex align-items-center" role="alert">
                      <i className="ti ti-info-circle me-2"></i>
                      <small>Arraste pelo ícone <i className="ti ti-grip-vertical"></i> para reordenar as etapas</small>
                    </div>
                  </div>
                )}

                <div
                  ref={newStagesContainerRef}
                  className="stages-container"
                  style={{
                    minHeight: '60px',
                    borderRadius: '6px',
                    padding: '8px'
                  }}
                >
                  {newFunnel.etapas.map((etapa, index) => (
                    <div
                      key={`new-stage-${index}`} // Chave única e estável
                      className="pipeline-item d-flex justify-content-between align-items-center"
                      data-index={index} // IMPORTANTE: adicionar data-index
                      style={{
                        marginBottom: '10px',
                        padding: '12px',
                        border: '1px solid #dee2e6',
                        borderRadius: '8px',
                        backgroundColor: 'white',
                        transition: 'all 0.2s ease',
                        cursor: 'default'
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <div
                          className="drag-handle me-3"
                          style={{
                            cursor: 'grab',
                            padding: '4px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <i className="ti ti-grip-vertical text-muted" />
                        </div>
                        <div>
                          <div className="d-flex align-items-center">
                            <span className="fw-medium">{etapa.nome}</span>
                            <small className="text-muted ms-2">#{index + 1}</small> {/* Indicador visual da ordem */}
                            {renderContratoIcon(etapa.gera_contrato || false)}
                          </div>
                          {etapa.descricao && (
                            <small className="text-muted d-block mt-1">{etapa.descricao}</small>
                          )}
                        </div>
                      </div>
                      <div className="action-pipeline">
                        <button
                          type="button"
                          className="btn btn-link text-danger p-1"
                          onClick={() => handleRemoveStage(index)}
                          title="Remover etapa"
                          style={{
                            border: 'none',
                            background: 'none',
                            fontSize: '16px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="offcanvas"
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Criando...
                  </>
                ) : (
                  'Criar'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal Editar Funil */}
      <div
        className="offcanvas offcanvas-end offcanvas-large"
        tabIndex={-1}
        id="offcanvas_edit"
      >
        <div className="offcanvas-header border-bottom">
          <h4>Editar Funil</h4>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            onClick={handleCancelEdit}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={(e) => {
            e.preventDefault();
            handleSaveEdit();
          }}>
            <div>
              <div className="mb-3">
                <label className="col-form-label">
                  Nome do Funil <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  type="text"
                  value={editingFunnel?.nome || ''}
                  onChange={(e) => handleFunnelChange('nome', e.target.value)}
                />
              </div>
              <div className="mb-3">
                <label className="col-form-label">
                  Descrição
                </label>
                <textarea
                  className="form-control"
                  value={editingFunnel?.descricao || ''}
                  onChange={(e) => handleFunnelChange('descricao', e.target.value)}
                />
              </div>
              <div className="mb-3">
                <label className="col-form-label">
                  Status
                </label>
                <select
                  className="form-control"
                  value={editingFunnel?.ativo ?? 1}
                  onChange={(e) => handleFunnelChange('ativo', Number(e.target.value))}
                >
                  <option value={1}>Ativo</option>
                  <option value={0}>Inativo</option>
                </select>
              </div>
              <div className="mb-3">
                <div className="pipe-title d-flex align-items-center justify-content-between">
                  <h5 className="form-title">Etapas do Funil</h5>
                  <button
                    type="button"
                    className="btn btn-link add-stage p-0"
                    data-bs-toggle="modal"
                    data-bs-target="#add_edit_funnel_stage"
                    onClick={() => setEditingStage({ nome: '', descricao: '', gera_contrato: false })}
                  >
                    <i className="ti ti-square-rounded-plus" />
                    Adicionar Nova
                  </button>
                </div>

                <div className="pipeline-listing">
                  {editingFunnelStages.length > 0 && (
                    <div className="mb-3">
                      <div className="alert alert-info d-flex align-items-center" role="alert">
                        <i className="ti ti-info-circle me-2"></i>
                        <small>Arraste pelo ícone <i className="ti ti-grip-vertical"></i> para reordenar as etapas</small>
                      </div>
                    </div>
                  )}

                  <div
                    ref={editStagesContainerRef}
                    className="stages-container"
                    style={{
                      minHeight: '60px',
                      borderRadius: '6px',
                      padding: '8px'
                    }}
                  >
                    {editingFunnelStages.map((etapa, index) => (
                      <div
                        key={etapa.id ? `edit-stage-${etapa.id}` : `new-edit-stage-${index}`} // Chave baseada no ID quando possível
                        className="pipeline-item d-flex justify-content-between align-items-center"
                        data-index={index} // IMPORTANTE: adicionar data-index
                        style={{
                          marginBottom: '10px',
                          padding: '12px',
                          border: '1px solid #dee2e6',
                          borderRadius: '8px',
                          backgroundColor: 'white',
                          transition: 'all 0.2s ease',
                          cursor: 'default'
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="drag-handle me-3"
                            style={{
                              cursor: 'move',
                              padding: '8px',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              userSelect: 'none'
                            }}
                          >
                            <i className="ti ti-grip-vertical text-muted" />
                          </div>
                          <div>
                            <div className="d-flex align-items-center">
                              <span className="fw-medium">{etapa.nome}</span>
                              <small className="text-muted ms-2">#{etapa.ordem || index + 1}</small> {/* Indicador visual da ordem */}
                              {renderContratoIcon(etapa.gera_contrato || false)}
                            </div>
                            {etapa.descricao && (
                              <small className="text-muted d-block mt-1">{etapa.descricao}</small>
                            )}
                          </div>
                        </div>
                        <div className="action-pipeline">
                          <button
                            type="button"
                            className="btn btn-link text-blue me-2 p-1"
                            data-bs-toggle="modal"
                            data-bs-target="#edit_stage"
                            onClick={() => handleEditStage(etapa)}
                            title="Editar etapa"
                            style={{
                              border: 'none',
                              background: 'none',
                              fontSize: '16px',
                              borderRadius: '4px',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <i className="ti ti-edit" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-link text-danger p-1"
                            onClick={() => handleRemoveStageFromFunnel(etapa.id || 0)}
                            title="Remover etapa"
                            style={{
                              border: 'none',
                              background: 'none',
                              fontSize: '16px',
                              borderRadius: '4px',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <i className="ti ti-trash" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="offcanvas"
                onClick={handleCancelEdit}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary">
                Salvar Alterações
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modais de sucesso, exclusão, etc. */}
      <div className="modal fade" id="delete_pipeline" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="text-center">
                <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                  <i className="ti ti-trash-x fs-36 text-danger" />
                </div>
                <h4 className="mb-2">Remover Funil?</h4>
                <p className="mb-0">
                  Tem certeza que deseja remover o funil selecionado?
                </p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                    disabled={isDeleting}
                    onClick={() => setDeletingFunnelId(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteFunnel}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Excluindo...
                      </>
                    ) : (
                      'Sim, Deletar'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade custom-modal" id="success_delete_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Funil Excluído com Sucesso!</h3>
                <p>O funil foi removido permanentemente do sistema.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => limparModal('success_delete_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade custom-modal" id="success_edit_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Funil Atualizado com Sucesso!</h3>
                <p>As alterações foram salvas com sucesso.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => limparModal('success_edit_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade custom-modal" id="success_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                {deletingFunnelId ? (
                  <>
                    <h3>Funil Excluído com Sucesso!</h3>
                    <p>O funil foi removido permanentemente do sistema.</p>
                  </>
                ) : (
                  <>
                    <h3>Funil Criado com Sucesso!</h3>
                    <p>O funil foi criado e já está disponível para uso.</p>
                  </>
                )}
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => limparModalESucesso('success_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Editar Etapa - CORRIGIDO */}
      <div className="modal custom-modal fade" id="edit_stage" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Editar Etapa</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => {
                e.preventDefault();
                handleSaveStageEdit();
              }}>
                <div className="mb-3">
                  <label className="col-form-label">
                    Nome da Etapa <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingStage.nome}
                    onChange={(e) => setEditingStage(prev => ({ ...prev, nome: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">
                    Descrição
                  </label>
                  <textarea
                    className="form-control"
                    value={editingStage.descricao || ''}
                    onChange={(e) => setEditingStage(prev => ({ ...prev, descricao: e.target.value }))}
                  />
                </div>

                {/* NOVO CAMPO - Checkbox para geração de contrato */}
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="gera-contrato-edit"
                      checked={editingStage.gera_contrato || false}
                      onChange={(e) => setEditingStage(prev => ({ ...prev, gera_contrato: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="gera-contrato-edit">
                      <i className="ti ti-file-invoice me-1"></i>
                      Gerar contrato automaticamente quando oportunidade for "Ganha"
                    </label>
                  </div>
                  <small className="form-text text-muted">
                    Quando uma oportunidade nesta etapa for marcada como "Ganha", será oferecida a opção de criar um contrato automaticamente.
                  </small>
                </div>

                <div className="modal-btn text-end">
                  <button type="button" className="btn btn-light me-2" data-bs-dismiss="modal">
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Salvar Alterações
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Adicionar Nova Etapa ao Funil em Edição */}
      <div className="modal custom-modal fade" id="add_edit_funnel_stage" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Adicionar Nova Etapa ao Funil</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => {
                e.preventDefault();
                handleAddStageToFunnel();
              }}>
                <div className="mb-3">
                  <label className="col-form-label">
                    Nome da Etapa <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingStage.nome}
                    onChange={(e) => setEditingStage(prev => ({ ...prev, nome: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">
                    Descrição
                  </label>
                  <textarea
                    className="form-control"
                    value={editingStage.descricao || ''}
                    onChange={(e) => setEditingStage(prev => ({ ...prev, descricao: e.target.value }))}
                  />
                </div>

                {/* NOVO CAMPO - Checkbox para geração de contrato */}
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="gera-contrato-add-edit"
                      checked={editingStage.gera_contrato || false}
                      onChange={(e) => setEditingStage(prev => ({ ...prev, gera_contrato: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="gera-contrato-add-edit">
                      <i className="ti ti-file-invoice me-1"></i>
                      Gerar contrato automaticamente quando oportunidade for "Ganha"
                    </label>
                  </div>
                  <small className="form-text text-muted">
                    Quando uma oportunidade nesta etapa for marcada como "Ganha", será oferecida a opção de criar um contrato automaticamente.
                  </small>
                </div>

                <div className="modal-btn text-end">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                    onClick={() => setEditingStage({ nome: '', descricao: '', gera_contrato: false })}
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Adicionar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Adicionar Nova Etapa ao criar novo funil */}
      <div className="modal custom-modal fade" id="add_stage" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Adicionar Nova Etapa</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal">
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => {
                e.preventDefault();
                handleAddStage();
              }}>
                <div className="mb-3">
                  <label className="col-form-label">
                    Nome da Etapa <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={currentStage.nome}
                    onChange={(e) => setCurrentStage(prev => ({ ...prev, nome: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">
                    Descrição
                  </label>
                  <textarea
                    className="form-control"
                    value={currentStage.descricao}
                    onChange={(e) => setCurrentStage(prev => ({ ...prev, descricao: e.target.value }))}
                  />
                </div>

                {/* NOVO CAMPO - Checkbox para geração de contrato */}
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="gera-contrato-new"
                      checked={currentStage.gera_contrato || false}
                      onChange={(e) => setCurrentStage(prev => ({ ...prev, gera_contrato: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="gera-contrato-new">
                      <i className="ti ti-file-invoice me-1"></i>
                      Gerar contrato automaticamente quando oportunidade for "Ganha"
                    </label>
                  </div>
                  <small className="form-text text-muted">
                    Quando uma oportunidade nesta etapa for marcada como "Ganha", será oferecida a opção de criar um contrato automaticamente.
                  </small>
                </div>

                <div className="modal-btn text-end">
                  <button type="button" className="btn btn-light me-2" data-bs-dismiss="modal">
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Adicionar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Estilos CSS para Dragula e Contratos */}
      <style>{`
        /* Estilos do Dragula */
        .gu-mirror {
          position: fixed !important;
          margin: 0 !important;
          z-index: 9999 !important;
          opacity: 0.8;
          transform: rotate(5deg);
          pointer-events: none;
        }
        
        .gu-hide {
          display: none !important;
        }
        
        .gu-unselectable {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
        }
        
        .gu-transit {
          opacity: 0.2;
        }
        
        /* Estilos personalizados */
        .drag-handle {
          transition: all 0.2s ease;
          cursor: move !important;
        }
        
        .drag-handle:hover {
          background-color: rgba(0, 123, 255, 0.1) !important;
          transform: scale(1.05);
        }
        
        .drag-handle:active {
          cursor: grabbing !important;
        }
        
        .pipeline-item {
          cursor: default;
          transition: all 0.2s ease;
        }
        
        .pipeline-item:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          border-color: #007bff !important;
        }
        
        .action-pipeline .btn:hover {
          background-color: rgba(0,0,0,0.1);
        }
        
        .stages-container {
          position: relative;
        }
        
        /* Feedback visual durante o arraste */
        .stages-container.gu-over {
          background-color: #f0f8ff !important;
          border: 2px dashed #007bff !important;
        }
        
        /* Estado de arraste */
        .pipeline-item.gu-transit {
          opacity: 0.3 !important;
          transform: scale(0.95);
        }
        
        /* Garante que o ícone seja clicável */
        .ti-grip-vertical {
          pointer-events: auto;
          font-size: 16px;
          color: #6c757d;
        }
        
        .drag-handle .ti-grip-vertical:hover {
          color: #007bff;
        }

        /* NOVOS ESTILOS PARA CONTRATOS */
        
        /* Estilos para o badge de contrato */
        .bg-success-light {
          background-color: rgba(40, 167, 69, 0.1) !important;
        }

        .text-success {
          color: #28a745 !important;
        }

        /* Animação sutil para o badge */
        .badge {
          transition: all 0.2s ease;
        }

        .pipeline-item:hover .badge {
          transform: scale(1.05);
        }

        /* Estilo para o checkbox de contrato */
        .form-check-input:checked {
          background-color: #28a745;
          border-color: #28a745;
        }

        .form-check-label {
          cursor: pointer;
        }

        /* Feedback visual melhorado */
        .form-check:hover .form-check-label {
          color: #495057;
        }

        /* Estilo específico para badge de contrato */
        .badge.bg-success-light {
          border: 1px solid rgba(40, 167, 69, 0.2);
          font-weight: 500;
          letter-spacing: 0.5px;
        }

        /* Hover effect no badge */
        .badge.bg-success-light:hover {
          background-color: rgba(40, 167, 69, 0.15) !important;
          transform: scale(1.02);
        }

        /* Ícone no badge */
        .badge .ti-file-invoice {
          font-size: 10px;
        }

        /* Responsividade do badge */
        @media (max-width: 768px) {
          .badge.bg-success-light {
            font-size: 9px;
            padding: 1px 4px;
          }
        }

        /* Estilo para form-check no modal */
        .modal .form-check {
          padding: 12px;
          background-color: #f8f9fa;
          border-radius: 8px;
          border: 1px solid #dee2e6;
          margin-bottom: 8px;
        }

        .modal .form-check:hover {
          background-color: #e9ecef;
          border-color: #28a745;
        }

        .modal .form-check-input:checked ~ .form-check-label {
          color: #28a745;
          font-weight: 500;
        }

        /* Texto de ajuda */
        .form-text {
          font-style: italic;
          margin-top: 8px;
        }

        /* Badge container */
        .d-flex.align-items-center .badge {
          white-space: nowrap;
        }
      `}</style>
    </>
  );
};